// @ts-nocheck
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Tooltip } from '@mui/material';
import { sx } from '../sx';
import CommitInput from './CommitInput';
import CohortCellCard from './CohortCellCard';
import RowTrendCard from './RowTrendCard';
import AddDriverModal from './AddDriverModal';

const NAME_MAX_SHARE = 0.45;

export default function PlanGrid({ vm }: { vm: any }) {
  const [addOpen, setAddOpen] = useState(false);
  const card = useRef(null);
  const grid = useRef(null);
  const table = useRef(null);
  const nameSig = (vm.rows || []).map(r => [r.name, r.src, r.unit, r.indent, r.size, r.fw].join('|')).join('\n') + (vm.canAddDriver ? '+' : '');
  useLayoutEffect(() => {
    const host = grid.current, el = table.current;
    if (!host || !el) return undefined;
    const fit = () => {
      el.style.setProperty('--gm-name-w', 'max-content');
      let need = 0;
      el.querySelectorAll('[data-name-cell]').forEach(c => { need = Math.max(need, Math.ceil(c.getBoundingClientRect().width)); });
      if (!need) return;
      const cap = host.clientWidth ? Math.round(host.clientWidth * NAME_MAX_SHARE) : need;
      el.style.setProperty('--gm-name-w', Math.min(need, cap) + 'px');
    };
    fit();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const watch = new ResizeObserver(fit);
    watch.observe(host);
    return () => watch.disconnect();
  }, [nameSig]);
  const lit = useRef([]);
  const clearSources = () => {
    lit.current.forEach(el => el.classList.remove('gmSrcActual', 'gmSrcForecast', 'gmSrcSelf'));
    lit.current = [];
  };
  const pinned = useRef(null);
  const light = (explainFn, el) => {
    clearSources();
    const explain = explainFn();
    if (!explain) return null;
    el.classList.add('gmSrcSelf');
    lit.current.push(el);
    if (grid.current) {
      (explain.highlights || []).forEach(h => {
        const target = grid.current.querySelector('[data-cell="' + h.key + '"]');
        if (!target || target === el) return;
        target.classList.add(h.tone === 'forecast' ? 'gmSrcForecast' : 'gmSrcActual');
        lit.current.push(target);
      });
    }
    return explain;
  };
  const hideCard = () => {
    pinned.current = null;
    clearSources();
    if (card.current) card.current.hide();
  };
  // Month view: hover marks the source cells and a click opens the card. Quarter and Year: hover opens the card.
  const hoverCell = (explainFn, el) => {
    if (pinned.current) return;
    const explain = light(explainFn, el);
    if (explain && vm.isRolled && card.current) card.current.show(explain, el);
  };
  const leaveCell = () => {
    if (pinned.current) return;
    clearSources();
    if (vm.isRolled && card.current) card.current.hide();
  };
  const clickCell = (explainFn, el) => {
    if (pinned.current === el) { hideCard(); return; }
    const explain = light(explainFn, el);
    if (!explain) return;
    pinned.current = el;
    if (card.current) card.current.show(explain, el);
  };
  useEffect(() => {
    const onDown = e => { if (pinned.current && !(e.target.closest && e.target.closest('[data-explain]'))) hideCard(); };
    const onKey = e => { if (e.key === 'Escape' && pinned.current) hideCard(); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, []);
  return (
    <>
<CohortCellCard ref={card} />
<RowTrendCard vm={vm} />
{addOpen && vm.canAddDriver ? <AddDriverModal sections={vm.addDriverSections || []} units={vm.addDriverUnits || []} isTaken={vm.driverNameTaken} onAdd={vm.onAddDriver} onClose={() => setAddOpen(false)} /> : null}
<div ref={grid} onScroll={hideCard} style={sx(`flex:1;min-height:0;overflow:auto`)}> <div ref={table} style={sx(`width:max-content;min-width:100%`)}> <div style={sx(`display:flex;position:sticky;top:0;z-index:3;background:#fff;box-shadow:0 1px 0 0 #DCE4F2`)}> <div data-name-cell style={sx(`flex:none;width:var(--gm-name-w,max-content);position:sticky;left:0;z-index:4;background:#fff;padding:0 14px;height:38px;display:flex;align-items:center;font-size:11px;font-weight:500;letter-spacing:0.14em;text-transform:uppercase;color:var(--ink-4);border-right:1px solid #DCE4F2;gap:10px`)}><span style={sx(`flex:1;min-width:0`)}>Driver</span>{vm.canAddDriver ? <button className="gmHover" onClick={() => (vm.addDriverEditor ? vm.newDriver() : setAddOpen(true))} style={sx(`flex:none;height:24px;padding:0 10px;border:1px solid #DCE4F2;border-radius:6px;background:#fff;color:var(--ink-4);font-family:inherit;font-size:11.5px;font-weight:400;letter-spacing:0;text-transform:none;cursor:pointer`)} data-hover="background:#F5F8FA;color:#0052CD;border-color:#0052CD">+ Add driver</button> : null}</div> {(vm.cols || []).map((m, mIdx) => (
<React.Fragment key={mIdx}>
 {m.range ? (
<Tooltip title={m.range} placement="bottom" arrow componentsProps={{ tooltip: { sx: { bgcolor: '#1f2937', fontSize: '12px', padding: '6px 10px', '& .MuiTooltip-arrow': { color: '#1f2937' } } } }}>
 <div style={sx(`flex:none;width:${vm.colW};height:38px;display:flex;align-items:center;justify-content:flex-end;padding-right:10px;font-size:11.5px;font-weight:500;white-space:nowrap;cursor:default;background:${m.bg};color:${m.fg}`)}>{m.label}</div>
</Tooltip>
) : (
 <div style={sx(`flex:none;width:${vm.colW};height:38px;display:flex;align-items:center;justify-content:flex-end;padding-right:10px;font-size:11.5px;font-weight:500;white-space:nowrap;background:${m.bg};color:${m.fg}`)}>{m.label}</div>
)} </React.Fragment>
))} {vm.isMonthGrain ? (
<>
 <div style={sx(`flex:none;width:32px;height:38px;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:1px;background:#fff;border-left:1px solid #EDF1F8`)}> <button className="gmHover" onClick={vm.addCol} title={vm.colNote} style={sx(`width:22px;height:16px;display:flex;align-items:center;justify-content:center;border:1px solid #DCE4F2;border-radius:4px;background:#fff;color:${vm.addColFg};font-family:inherit;font-size:11px;line-height:1;cursor:pointer`)} data-hover="border-color:#0052CD">+</button> <button className="gmHover" onClick={vm.dropCol} title={vm.dropNote} style={sx(`width:22px;height:16px;display:flex;align-items:center;justify-content:center;border:1px solid #DCE4F2;border-radius:4px;background:#fff;color:${vm.dropColFg};font-family:inherit;font-size:11px;line-height:1;cursor:pointer`)} data-hover="border-color:#0052CD">−</button> </div> </>
) : null} </div> {(vm.rows || []).map((row, rowIdx) => (
<React.Fragment key={rowIdx}>
 <div style={sx(`display:flex;border-bottom:1px solid #F0F4FA;border-top:${row.topBorder}`)}> <div data-name-cell onClick={row.onClick} style={sx(`flex:none;width:var(--gm-name-w,max-content);position:sticky;left:0;z-index:2;padding:0 14px 0 ${row.indent};height:var(--rh,34px);display:flex;align-items:center;gap:8px;cursor:${row.cursor};border-right:1px solid #DCE4F2;background:${row.nameBg}`)}> <span style={sx(`flex:1;min-width:0;overflow:${row.labelOverflow};text-overflow:${row.labelClip};white-space:nowrap;font-size:${row.size};letter-spacing:${row.ls};text-transform:${row.tt};color:${row.nameFg};font-weight:${row.fw}`)}><span onMouseEnter={row.onHover || undefined} onMouseLeave={row.onLeave || undefined}>{row.name}</span></span> {row.hasTag ? (
<>
 <span onClick={row.onSrc} title={row.srcTitle} style={sx(`flex:none;font-size:10px;font-weight:500;letter-spacing:0.08em;text-transform:uppercase;color:${row.srcFg};background:${row.srcBg};padding:2px 5px;border-radius:4px;cursor:${row.srcCursor}`)}>{row.src}</span> <span style={sx(`flex:none;width:14px;text-align:center;font-size:11px;color:var(--ink-4)`)}>{row.unit}</span> </>
) : null} </div> {(row.cells || []).map((cell, cellIdx) => (
<React.Fragment key={cellIdx}>
 <div data-cell={cell.cellKey || undefined} title={cell.explain ? undefined : cell.skipNote || undefined} data-explain={cell.explain ? '1' : undefined} onMouseEnter={cell.explain ? e => hoverCell(cell.explain, e.currentTarget) : undefined} onMouseLeave={cell.explain ? leaveCell : undefined} onClick={cell.explain ? e => clickCell(cell.explain, e.currentTarget) : undefined} style={sx(`flex:none;position:relative;width:${vm.colW};height:var(--rh,34px);display:flex;align-items:center;justify-content:flex-end;background:${cell.bg};border-left:1px solid #F5F8FA;cursor:${cell.explain ? 'pointer' : 'default'}`)}> {cell.skipNote ? <span data-testid="pl-skip-mark" style={sx(`position:absolute;top:0;right:0;width:0;height:0;border-top:7px solid #F5A623;border-left:7px solid transparent;pointer-events:none`)}></span> : null} {cell.isInput ? (
<>
 <div style={sx(`display:flex;flex-direction:column;align-items:stretch;justify-content:center;width:100%;height:100%`)}> <CommitInput className="gmFocus" value={cell.text} onCommit={cell.onCommit || cell.onChange} onPaste={cell.onPaste} style={sx(`width:100%;height:24px;flex:none;border:none;border-radius:3px;background:none;text-align:right;padding:0 10px;font-family:inherit;font-size:12.5px;font-variant-numeric:tabular-nums;color:var(--ink);font-weight:${cell.fw};outline:none;box-shadow:inset 0 0 0 1px transparent;transition:box-shadow 160ms cubic-bezier(.22,1,.36,1)`)} /> {(cell.deltas || []).map((p, pIdx) => (
<React.Fragment key={pIdx}>
 <span style={sx(`text-align:right;padding-right:10px;font-size:${p.size};font-variant-numeric:tabular-nums;color:${p.fg};font-weight:${p.fw};line-height:1.25`)}>{p.text}</span> </React.Fragment>
))} </div> </>
) : null} {cell.isText ? (
<>
 <div style={sx(`display:flex;flex-direction:column;align-items:flex-end;gap:1px;padding-right:10px`)}> {(cell.parts || []).map((p, pIdx) => (
<React.Fragment key={pIdx}>
 <span style={sx(`font-size:${p.size};font-variant-numeric:tabular-nums;color:${p.fg};font-weight:${p.fw};line-height:1.25`)}>{p.text}</span> </React.Fragment>
))} </div> </>
) : null} </div> </React.Fragment>
))} </div> </React.Fragment>
))} </div> </div> 
    </>
  );
}
